import { memo, useRef, useState } from "react";
import TagValueCell from "./TagValueCell";
import EsgPillarPicker from "./EsgPillarPicker";
import { pillarOption } from "../../../../../_constants/esgPillars";

/**
 * The module position of a fixed tag path: the tag value, plus the ESG pillar the module tag sits under. The
 * pillar belongs to the module tag, not to this KPI, so changing it moves every KPI tagged with it. A module tag
 * created here asks for its pillar straight away; dismissing the question creates it without one, and the badge
 * keeps saying so.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {string[]} props.options module tags already in the vocabulary
 * @param {boolean} [props.dirty] changed and not yet saved
 * @param {boolean} [props.disabled]
 * @param {string} props.ariaLabel
 * @param {string} [props.pillar] the picked module tag's pillar
 * @param {(value: string) => void} props.onChange
 * @param {(moduleTag: string) => string|undefined} props.pillarOf the pillar a module tag already has, in any spelling
 * @param {(value: string, esgPillar: string|null) => void} props.onFile files the new module tag with its pillar
 * @param {(moduleTag: string, esgPillar: string) => Promise<string|null>} props.onSetPillar resolves to why it failed, or null
 */
function ModuleTagCell({ value, options, dirty, disabled, ariaLabel, pillar, pillarOf, onChange, onFile, onSetPillar }) {
  const anchorRef = useRef(null);
  const [asking, setAsking] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const option = pillarOption(pillar);

  const ask = (moduleTag, creating) => {
    setError("");
    setAsking({ moduleTag, creating });
  };

  /** A new spelling of a tag that already has a pillar takes that pillar, so only a tag new to the vocabulary asks. */
  const create = (moduleTag) => {
    onChange(moduleTag);
    if (pillarOf(moduleTag)) onFile(moduleTag, null);
    else ask(moduleTag, true);
  };

  const pick = async (esgPillar) => {
    if (asking.creating) {
      setAsking(null);
      onFile(asking.moduleTag, esgPillar);
      return;
    }
    setSaving(true);
    const failure = await onSetPillar(asking.moduleTag, esgPillar);
    setSaving(false);
    if (failure) setError(failure);
    else setAsking(null);
  };

  const dismiss = () => {
    if (asking.creating) onFile(asking.moduleTag, null);
    setAsking(null);
  };

  return (
    <div className="kgb-moduletag" ref={anchorRef}>
      <TagValueCell
        value={value}
        options={options}
        dirty={dirty}
        disabled={disabled}
        ariaLabel={ariaLabel}
        onChange={onChange}
        onCreate={create}
      />
      {value ? (
        <button
          type="button"
          className={option ? "kgb-esg-badge" : "kgb-esg-badge kgb-esg-badge--unset"}
          disabled={disabled || !!asking}
          aria-haspopup="true"
          aria-expanded={!!asking}
          aria-label={option ? `ESG pillar of ${value}: ${option.label}` : `Set ESG pillar of ${value}`}
          title={option ? option.label : "Set the ESG pillar"}
          onClick={() => ask(value, false)}
        >
          {option ? option.short : "Set ESG"}
        </button>
      ) : null}
      {asking ? (
        <EsgPillarPicker
          anchorRef={anchorRef}
          moduleTag={asking.moduleTag}
          current={asking.creating ? undefined : pillar}
          error={error}
          saving={saving}
          onPick={pick}
          onClose={dismiss}
        />
      ) : null}
    </div>
  );
}

export default memo(ModuleTagCell);
